import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { organizationsService } from '../services/api';
import './Organizations.css';
import { useAuth } from '../contexts/AuthContext';
import PhoneEditor from './PhoneEditor';
import AddressEditor from './AddressEditor';

const Organizations = () => {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phones: [],
    addresses: [],
  });
  const { isAdmin } = useAuth();
  const itemsPerPage = 20;

  const fetchOrganizations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await organizationsService.list({
        page,
        limit: itemsPerPage,
        search: searchTerm || undefined
      });

      setOrganizations(response.data.data || response.data.organizations || []);
      setTotal(response.data.total || response.data.pagination?.total || 0);
      setTotalPages(response.data.totalPages || response.data.pagination?.pages || 1);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch organizations');
      console.error('Error fetching organizations:', err);
    } finally {
      setLoading(false);
    }
  }, [page, searchTerm, itemsPerPage]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handleAddClick = () => {
    setEditingId(null);
    setFormData({
      name: '',
      phones: [],
      addresses: [],
    });
    setShowForm(true);
  };

  const handlePhonesChange = (phones) => {
    setFormData(prev => ({
      ...prev,
      phones
    }));
  };

  const handleAddressesChange = (addresses) => {
    setFormData(prev => ({
      ...prev,
      addresses
    }));
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await organizationsService.update(editingId, formData);
        alert('Organization updated successfully');
      } else {
        await organizationsService.create(formData);
        alert('Organization created successfully');
      }
      setShowForm(false);
      setPage(1);
      fetchOrganizations();
    } catch (err) {
      alert('Error saving organization: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this organization?')) {
      try {
        await organizationsService.delete(id);
        setOrganizations(organizations.filter(org => org.id !== id));
        alert('Organization deleted successfully');
      } catch (err) {
        alert('Failed to delete organization: ' + err.message);
      }
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingId(null);
  };

  if (error && !loading) {
    return (
      <div className="organizations-container">
        <div className="alert alert-error">{error}</div>
      </div>
    );
  }

  return (
    <div className="organizations-container">
      <div className="page-header">
        <h1>
          Organizations
          <button className="btn btn-primary" onClick={handleAddClick}>
            + Organization
          </button>
        </h1>
      </div>

      {/* Search */}
      <div className="filters-section">
        <div className="filter-group">
          <input
            type="text"
            className="search-input"
            placeholder="Search organizations by name or email..."
            value={searchTerm}
            onChange={handleSearch}
          />
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={handleCloseForm}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingId ? 'Edit Organization' : 'Add Organization'}</h2>
              <button className="close-btn" onClick={handleCloseForm}>×</button>
            </div>
            <form onSubmit={handleFormSubmit} className="organization-form">
              <div className="form-group">
                <label htmlFor="name">Organization Name *</label>
                <input
                  id="name"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleFormChange}
                  required
                  placeholder="Enter organization name"
                />
              </div>

              <div className="form-section">
                <h3>Contact Information</h3>
                
                <PhoneEditor
                  phones={formData.phones}
                  onChange={handlePhonesChange}
                />
                
                <AddressEditor
                  addresses={formData.addresses}
                  onChange={handleAddressesChange}
                />
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={handleCloseForm}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingId ? 'Update' : 'Create'} Organization
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Organizations List */}
      {loading ? (
        <div className="loading">Loading organizations...</div>
      ) : organizations.length === 0 ? (
        <div className="alert alert-info">
          No organizations found. {searchTerm && 'Try adjusting your search.'}
        </div>
      ) : (
        <>
          <div className="table-container">
            <table className="organizations-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Phone</th>
                  {isAdmin && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {organizations.map(org => (
                  <tr key={org.id} className="table-row" 
                    onClick={() => navigate(`/organizations/${org.id}`)}>
                    <td className="name-cell">
                      {org.name}
                    </td>
                    <td>{org.phone ? org.phone : '-'}</td>
                    {isAdmin && <td className="actions-cell">
                      <button 
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDelete(org.id)}
                        title="Delete"
                      >
                        Delete
                      </button>
                    </td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="btn btn-secondary"
                onClick={() => setPage(prev => Math.max(1, prev - 1))}
                disabled={page === 1}
              >
                Previous
              </button>
              <span className="page-info">
                Page {page} of {totalPages} (Total: {total})
              </span>
              <button
                className="btn btn-secondary"
                onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
                disabled={page === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Organizations;
